import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
  School as TeacherIcon
} from '@mui/icons-material';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import './App.css';

function Login() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('admin');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deviceId, setDeviceId] = useState('');
  
  // Header state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Close mobile menu when clicking a link
  const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Scroll smoothly to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigate to home page and scroll to contact section
  const handleRequestDemo = () => {
    handleNavLinkClick();
    navigate('/#contact');
  };

  // Enhanced scroll behavior for header and back-to-top
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setShowBackToTop(currentScrollY > 200);
      setIsHeaderScrolled(currentScrollY > 50);
      
      if (currentScrollY > 150) {
        if (currentScrollY > lastScrollY) {
          setHeaderVisible(false);
        } else {
          setHeaderVisible(true);
        }
      } else {
        setHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setRole(newValue === 0 ? 'admin' : 'teacher');
    setError('');
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔥 Starting login with:', { email, selectedRole: role });
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('✅ Firebase Auth successful, user UID:', user.uid);

      if (role === 'teacher') {
        // Check that the email exists in the devices collection
        const deviceQ = query(collection(db, 'devices'), where('email', '==', email));
        const deviceSnap = await getDocs(deviceQ);
        if (deviceSnap.empty) {
          setError('This device is not registered. Please check your device email.');
          setLoading(false);
          setPassword('');
          return;
        }
        // If found, proceed to redirect
        const token = await user.getIdToken();
        const targetUrl = `https://teacher.safelinepickup.com?token=${token}`;
        window.location.href = targetUrl;
        return;
      }

      // Admin login logic (role check)
      const q = query(collection(db, 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        setError('No user account found.');
        setLoading(false);
        setPassword('');
        return;
      }
      const userData = querySnapshot.docs[0].data();
      let userRole = userData.role;
      if (role === 'admin' && (userRole === 'admin' || userRole === 'school_admin')) {
        const token = await user.getIdToken();
        const targetUrl = `https://admin.safelinepickup.com?token=${token}`;
        window.location.href = targetUrl;
        return;
      } else {
        setError('Invalid role selected for this account.');
        setLoading(false);
        setPassword('');
        return;
      }
    } catch (err) {
      setError('Invalid email, password, or device. Please try again.');
      setLoading(false);
      setPassword('');
    }
  };

  return (
    <div className="app-container">
      {/* Header */}
      <header className={`header ${isHeaderScrolled ? 'scrolled' : ''} ${headerVisible ? 'visible' : 'hidden'}`}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <motion.div
            className="logo"
            whileHover={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            SafeLine Pickup
          </motion.div>
        </Link>

        {/* Navigation links */}
        <nav className={isMenuOpen ? 'nav-mobile-open' : ''}>
          <ul className="nav-links">
            <li>
              <Link to="/#origin" onClick={handleNavLinkClick}>Product</Link>
            </li>
            <li>
              <Link to="/team" onClick={handleNavLinkClick}>Team</Link>
            </li>
            <li>
              <a href="#" onClick={(e) => { e.preventDefault(); handleRequestDemo(); }}>
                Request a Demo
              </a>
            </li>
          </ul>
          
          {/* Login Button - appears in mobile menu only */}
          <motion.div
            className="header-login-container mobile-only"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/login" className="login-button-header" onClick={handleNavLinkClick}>
              <span>Login</span>
            </Link>
          </motion.div>
        </nav>

        {/* Hamburger icon for mobile */}
        <div
          className={`hamburger ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span />
          <span />
          <span />
        </div>

        {/* Login Button - desktop only */}
        <motion.div
          className="header-login-container desktop-only"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link to="/login" className="login-button-header">
            <span>Login</span>
          </Link>
        </motion.div>
      </header>

      {/* Login Section */}
      <section className="login-section" style={{ paddingTop: '120px' }}>
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 2
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Tabs value={tab} onChange={handleTabChange} centered>
                <Tab 
                  label="Admin Login" 
                  icon={<AdminIcon />} 
                  iconPosition="start" 
                />
                <Tab 
                  label="Teacher Login" 
                  icon={<TeacherIcon />} 
                  iconPosition="start" 
                />
              </Tabs>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Welcome Back
              </Typography>
              
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                Sign in to your SafeLine {tab === 0 ? 'admin' : 'teacher'} account
              </Typography>
            </Box>
            
            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                variant="outlined"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              
              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={toggleShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ 
                  py: 1.5,
                  mt: 3,
                  mb: 2,
                  position: 'relative',
                  background: 'linear-gradient(90deg, #ff6b35, #ff9500)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #e55100, #ff6b35)',
                  }
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                      color: 'white'
                    }}
                  />
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                OR
              </Typography>
            </Divider>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Need help accessing your account?
              </Typography>
              
              <Button
                component={Link}
                to="/"
                variant="outlined"
                color="primary"
                fullWidth
                sx={{ mt: 1 }}
              >
                Back to Home
              </Button>
            </Box>
          </Paper>
        </Container>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-logo">SafeLine Pickup</div>
        <p>© {new Date().getFullYear()} SafeLine Pickup. All rights reserved.</p>
      </footer>

      {/* "Back to Top" button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            className="back-to-top"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ y: -5 }}
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Login; 